import type {
  FetchArgs,
  FetchBaseQueryError,
  QueryReturnValue,
} from '@reduxjs/toolkit/query';
import { useCallback } from 'react';
import { crmApi } from './crm.feature';

export interface TeamsWebhook {
  id: string;
  name: string;
  hint: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface TeamsStatus {
  connected: boolean;
  canManage: boolean;
  webhooks: TeamsWebhook[];
}

export interface TeamsBotTenant {
  tenantId: string;
  tenantName: string | null;
  connectedAt: string;
}

export interface TeamsBotStatus {
  configured: boolean;
  teamsAppId: string | null;
  tenant: TeamsBotTenant | null;
  canManage: boolean;
  /** False for a connected org that lost the plan: it can still disconnect. */
  entitled: boolean;
}

export interface TeamsBotChannel {
  id: string;
  name: string;
  teamName: string;
}

/** Whether the signed-in member's Teams account is linked to Clu. */
export interface TeamsLinkStatus {
  linked: boolean;
  teamsUserId?: string | null;
  tenantName?: string | null;
}

type TeamsLinkScope = { organizationId: string; userId: string };

/** Who asked Clu for a Teams link, from the signed state the API verified. */
export interface TeamsHandoffPreview {
  tenantId: string;
  tenantName: string | null;
  requesterName: string | null;
  requesterEmail: string | null;
}

export interface TeamsConnectPreview extends TeamsHandoffPreview {
  canManage: boolean;
  emailMatches: boolean;
}

export interface TeamsLinkResult {
  tenantId: string;
  tenantName: string | null;
}

type SignedStateArgs = { organizationId: string; requestId: string };

const headers = (organizationId: string) => ({
  'X-Cleanlist-Expected-Org': organizationId,
});
const tag = (organizationId: string) => ({
  type: 'Teams' as const,
  id: organizationId,
});

// Webhook URLs are credentials. Only an opaque request id enters Redux.
const pendingWebhooks = new Map<
  string,
  { organizationId: string; name: string; url: string }
>();
// Signed Teams link states are credentials. Only an opaque request id enters
// Redux; the state itself goes straight from this map into the request body.
const pendingSignedStates = new Map<
  string,
  { organizationId: string; state: string }
>();

function postSignedState<T>(url: string) {
  return async (
    { organizationId, requestId }: SignedStateArgs,
    _api: unknown,
    _options: unknown,
    baseQuery: (arg: string | FetchArgs) => unknown
  ) => {
    const pending = pendingSignedStates.get(requestId);
    pendingSignedStates.delete(requestId);
    if (!pending || pending.organizationId !== organizationId)
      return {
        error: {
          status: 'CUSTOM_ERROR' as const,
          error: 'Teams link request expired.',
        },
      };
    const result = (await baseQuery({
      url,
      method: 'POST',
      headers: headers(organizationId),
      body: { state: pending.state },
    })) as QueryReturnValue<unknown, FetchBaseQueryError, unknown>;
    // Fetch metadata contains the signed token in the request body.
    return result.error ? { error: result.error } : { data: result.data as T };
  };
}

export const teamsApi = crmApi
  .enhanceEndpoints({ addTagTypes: ['Teams'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getTeamsStatus: builder.query<TeamsStatus, string>({
        query: (organizationId) => ({
          url: '/integrations/teams',
          headers: headers(organizationId),
        }),
        providesTags: (_result, _error, organizationId) => [
          tag(organizationId),
        ],
      }),
      addTeamsWebhook: builder.mutation<
        TeamsWebhook,
        { organizationId: string; requestId: string }
      >({
        async queryFn(
          { organizationId, requestId },
          _api,
          _options,
          baseQuery
        ) {
          const webhook = pendingWebhooks.get(requestId);
          pendingWebhooks.delete(requestId);
          if (!webhook || webhook.organizationId !== organizationId)
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'Teams channel request expired.',
              },
            };
          const result = await baseQuery({
            url: '/integrations/teams/webhooks',
            method: 'POST',
            headers: headers(organizationId),
            body: { name: webhook.name, url: webhook.url },
          });
          // Drop fetch metadata too: it contains the request with its URL.
          return result.error
            ? { error: result.error }
            : { data: result.data as TeamsWebhook };
        },
        invalidatesTags: (_result, error, { organizationId }) =>
          error ? [] : [tag(organizationId)],
      }),
      removeTeamsWebhook: builder.mutation<
        void,
        { organizationId: string; id: string }
      >({
        query: ({ organizationId, id }) => ({
          url: `/integrations/teams/webhooks/${encodeURIComponent(id)}`,
          method: 'DELETE',
          headers: headers(organizationId),
        }),
        invalidatesTags: (_result, error, { organizationId }) =>
          !error || error.status === 404 ? [tag(organizationId)] : [],
      }),
      getTeamsBot: builder.query<TeamsBotStatus, string>({
        query: (organizationId) => ({
          url: '/integrations/teams/bot',
          headers: headers(organizationId),
        }),
        providesTags: (_result, _error, organizationId) => [
          tag(organizationId),
        ],
      }),
      getTeamsLinkStatus: builder.query<TeamsLinkStatus, TeamsLinkScope>({
        query: ({ organizationId }) => ({
          url: '/integrations/teams/bot/link/status',
          headers: headers(organizationId),
        }),
        providesTags: (_result, _error, { organizationId }) => [
          tag(organizationId),
        ],
      }),
      getTeamsBotChannels: builder.query<
        { channels: TeamsBotChannel[] },
        string
      >({
        query: (organizationId) => ({
          url: '/integrations/teams/bot/channels',
          headers: headers(organizationId),
        }),
        providesTags: (_result, _error, organizationId) => [
          tag(organizationId),
        ],
      }),
      connectTeamsBot: builder.mutation<TeamsBotTenant, SignedStateArgs>({
        queryFn: postSignedState<TeamsBotTenant>(
          '/integrations/teams/bot/connect'
        ),
        invalidatesTags: (_result, error, { organizationId }) =>
          error ? [] : [tag(organizationId)],
      }),
      previewTeamsConnect: builder.mutation<
        TeamsConnectPreview,
        SignedStateArgs
      >({
        queryFn: postSignedState<TeamsConnectPreview>(
          '/integrations/teams/bot/connect/preview'
        ),
      }),
      previewTeamsLink: builder.mutation<TeamsHandoffPreview, SignedStateArgs>({
        queryFn: postSignedState<TeamsHandoffPreview>(
          '/integrations/teams/bot/link/preview'
        ),
      }),
      linkTeamsAccount: builder.mutation<TeamsLinkResult, SignedStateArgs>({
        queryFn: postSignedState<TeamsLinkResult>(
          '/integrations/teams/bot/link'
        ),
      }),
      disconnectTeamsBot: builder.mutation<void, { organizationId: string }>({
        query: ({ organizationId }) => ({
          url: '/integrations/teams/bot',
          method: 'DELETE',
          headers: headers(organizationId),
        }),
        invalidatesTags: (_result, error, { organizationId }) =>
          !error || error.status === 404 ? [tag(organizationId)] : [],
      }),
    }),
  });

export const {
  useGetTeamsStatusQuery,
  useRemoveTeamsWebhookMutation,
  useGetTeamsBotQuery,
  useDisconnectTeamsBotMutation,
  useGetTeamsBotChannelsQuery,
  useGetTeamsLinkStatusQuery,
} = teamsApi;

/** Add a Teams channel without retaining its webhook URL in Redux. */
export function useAddTeamsWebhookMutation() {
  const [submit, state] = teamsApi.useAddTeamsWebhookMutation();
  const add = useCallback(
    (organizationId: string, name: string, url: string) => {
      const requestId = crypto.randomUUID();
      pendingWebhooks.set(requestId, { organizationId, name, url });
      const request = submit({ organizationId, requestId });
      void request.finally(() => pendingWebhooks.delete(requestId));
      return request;
    },
    [submit]
  );
  return [add, state] as const;
}

/** A signed-state call that keeps the Teams link token out of Redux. */
function useSignedStateMutation<T>(
  trigger: (args: SignedStateArgs) => {
    unwrap: () => Promise<T>;
    finally: (callback: () => void) => unknown;
  }
) {
  return useCallback(
    (organizationId: string, signedState: string) => {
      const requestId = crypto.randomUUID();
      pendingSignedStates.set(requestId, {
        organizationId,
        state: signedState,
      });
      const request = trigger({ organizationId, requestId });
      void request.finally(() => pendingSignedStates.delete(requestId));
      return request;
    },
    [trigger]
  );
}

/** Connect Teams without retaining its signed link token in Redux. */
export function useConnectTeamsBotMutation() {
  const [submit, state] = teamsApi.useConnectTeamsBotMutation();
  return [useSignedStateMutation(submit), state] as const;
}

export function usePreviewTeamsConnectMutation() {
  const [submit, state] = teamsApi.usePreviewTeamsConnectMutation();
  return [useSignedStateMutation(submit), state] as const;
}

export function usePreviewTeamsLinkMutation() {
  const [submit, state] = teamsApi.usePreviewTeamsLinkMutation();
  return [useSignedStateMutation(submit), state] as const;
}

export function useLinkTeamsAccountMutation() {
  const [submit, state] = teamsApi.useLinkTeamsAccountMutation();
  return [useSignedStateMutation(submit), state] as const;
}
